import { BellIcon, ChevronDownIcon, HamburgerIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Divider,
  Flex,
  HStack,
  IconButton,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { updateShowTermsStatus } from 'actions/home';
import LanguageSelector from 'components/LanguageSelector';
import ThemeSwitch from 'components/ThemeSwitch';
import Notification from 'containers/Notification';
import User from 'containers/User';
import { ROLES, UPLOAD_FILES_STATUS } from 'helpers/enum';
import React, { isValidElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link as ReactLink } from 'react-router-dom';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';

import { MobileNavContent } from './MobileNavContent';
import { homeAction } from '../../actions';
import { useLogout } from '../../contexts/LogoutContext';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { checkAuthenticationAdmin, checkAuthenticationSuperAdmin } from '../../helpers/lib';
import { getStorage, STORAGE_KEY } from '../../helpers/storage';
import { useNotification } from '../../hooks';
import { NotificationService } from '../../services/notification/notification.service';

export const Template = (props) => {
  const { t } = useTranslation();
  const {
    unsubscribeFromNotifications: _unsubscribeFromNotifications,
    unregisterServiceWorker: _unregisterServiceWorker,
  } = useNotification();
  const { logout } = useLogout();
  const { hasNewNotification, clearNewNotificationFlag } = useNotificationContext();

  const { num_as_read } = useSelector((state) => state.home);
  const [isOpen, setIsOpen] = useState(false);
  const [isCompanyManagementOpen, setIsCompanyManagementOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState({ all: 0, unread: 0 });

  // Check if auth is available
  const tokenAvailable = getStorage(STORAGE_KEY.TOKEN) && getStorage(STORAGE_KEY.USER);
  const children = React.Children.toArray(props.children).filter(isValidElement);
  const onClose = () => {
    setIsOpen(false);
    // Clear new notification flag when closing drawer
    clearNewNotificationFlag();
    // Reload notification count when closing modal
    loadNotificationCount();
  };

  // Load notification count
  const loadNotificationCount = useCallback(async () => {
    try {
      const count = await NotificationService.getNotificationCount();
      setNotificationCount(count);
    } catch (error) {
      console.error('Failed to load notification count:', error);
    }
  }, []);

  // Load notification count on mount
  useEffect(() => {
    loadNotificationCount();
  }, [loadNotificationCount]);

  // Refresh notification count when there's a new push notification
  useEffect(() => {
    if (hasNewNotification) {
      loadNotificationCount();
      clearNewNotificationFlag();
    }
  }, [hasNewNotification, loadNotificationCount, clearNewNotificationFlag]);
  const onCompanyManagementClose = () => {
    setIsCompanyManagementOpen(false);
  };

  const { status } = useSelector((state) => state.upload);

  const isUploading = status === UPLOAD_FILES_STATUS.UPLOADING;
  const mobileNav = useDisclosure();
  const dispatch = useDispatch();
  const location = useLocation();
  const isShowingTerms = useSelector((state) => state.home.isShowingTerms);

  // Separate conditions for upload and company tabs
  const isUploadTab =
    !isShowingTerms &&
    ['/upload', '/sync-sharepoint', '/synchronization'].includes(location?.pathname);
  const isCompanyTab =
    ['/admin/users', '/change-password'].includes(location?.pathname) || isShowingTerms;

  const isAdmin =
    tokenAvailable &&
    (checkAuthenticationAdmin() === ROLES.ADMIN ||
      checkAuthenticationSuperAdmin() === ROLES.SUPER_ADMIN);

  // Only show Upload dropdown for ADMIN/SUPER_ADMIN
  // USER will use Upload from router
  const canUpload = tokenAvailable && isAdmin;

  return (
    <Box>
      <Flex align='center' justify='space-between' bg='transparent'>
        {children.find((child) => child.type === Brand)?.props.children}
        <Flex align='center'>
          <HStack
            display={{
              base: 'none',
              md: 'flex',
            }}
            spacing={'10px'}
          >
            {children.find((child) => child.type === Links)?.props.children}
          </HStack>
          <HStack
            display={{
              base: 'none',
              md: 'flex',
            }}
            ml='10px'
            spacing={3}
          >
            {children.find((child) => child.type === UserProfile)?.props.children}
          </HStack>
          {canUpload ? (
            <>
              <Popover placement='bottom-end'>
                {({ isOpen, onClose }) => (
                  <>
                    <PopoverTrigger>
                      <Link
                        className={'dropdown-custom'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        display={{
                          base: 'none',
                          md: 'flex',
                        }}
                        mr={'10px'}
                        px={{ base: '0.625rem', xl: '0.9rem' }}
                        py={{ base: '0.625rem', xl: '0.8rem' }}
                        fontSize={{ base: 'small', md: '16px' }}
                        lineHeight={{ base: '0.5rem', lg: '1.25rem' }}
                        borderRadius='md'
                        transition='all 0.3s'
                        aria-current={isOpen ? 'page' : undefined}
                        variant={isUploadTab ? 'selected' : 'default'}
                      >
                        {t('route.upload')}
                        <ChevronDownIcon display={{ base: 'none', md: 'none', lg: 'block' }} />
                      </Link>
                    </PopoverTrigger>
                    <Portal>
                      <PopoverContent
                        zIndex='999 !important'
                        w='220px'
                        border='none'
                        borderRadius='12px'
                        _focus={{
                          outline: 0,
                        }}
                      >
                        <PopoverBody bg='bg_main_input' border='none' borderRadius='10px'>
                          <DropDownNavLink
                            fontWeight='normal'
                            w='100%'
                            as={ReactLink}
                            to={isUploading ? '#' : '/upload'}
                            cursor={isUploading ? 'not-allowed' : 'pointer'}
                            onClick={onClose}
                          >
                            {t('route.manualUpload')}
                          </DropDownNavLink>
                          {isAdmin ? (
                            <>
                              <DropDownNavLink
                                fontWeight='normal'
                                w='100%'
                                as={ReactLink}
                                to={isUploading ? '#' : '/sync-sharepoint'}
                                cursor={isUploading ? 'not-allowed' : 'pointer'}
                                onClick={onClose}
                              >
                                {t('route.connection')}{' '}
                                <Badge
                                  ml='2'
                                  fontSize={'xx-small'}
                                  textTransform={'capitalize'}
                                  colorScheme='green'
                                  rounded={4}
                                >
                                  {t('common.beta')}
                                </Badge>
                              </DropDownNavLink>
                              <DropDownNavLink
                                fontWeight='normal'
                                w='100%'
                                as={ReactLink}
                                to={isUploading ? '#' : '/synchronization'}
                                cursor={isUploading ? 'not-allowed' : 'pointer'}
                                onClick={onClose}
                              >
                                {t('route.synchronization')}
                                <Badge
                                  ml='2'
                                  fontSize={'xx-small'}
                                  textTransform={'capitalize'}
                                  colorScheme='green'
                                >
                                  {t('common.beta')}
                                </Badge>
                              </DropDownNavLink>
                            </>
                          ) : null}
                        </PopoverBody>
                      </PopoverContent>
                    </Portal>
                  </>
                )}
              </Popover>
            </>
          ) : null}
          {checkAuthenticationSuperAdmin() && (
            <React.Fragment key={'management-system'}>
              <NavLink
                display={{
                  base: 'none',
                  md: 'block',
                }}
                as={ReactLink}
                to={'/management-system'}
                key={'management-system'}
                isActive={location.pathname === '/management-system'}
              >
                {t('route.managementSystem')}
              </NavLink>
            </React.Fragment>
          )}

          <Popover placement='bottom-end'>
            {({ isOpen, onClose }) => (
              <>
                <PopoverTrigger>
                  <Link
                    className={'dropdown-custom'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={{
                      base: 'none',
                      md: 'flex',
                    }}
                    mr={'10px'}
                    px={{ base: '0.625rem', xl: '0.9rem' }}
                    py={{ base: '0.625rem', xl: '0.8rem' }}
                    fontSize={{ base: 'small', md: '16px' }}
                    lineHeight={{ base: '0.5rem', lg: '1.25rem' }}
                    borderRadius='md'
                    transition='all 0.3s'
                    aria-current={isOpen ? 'page' : undefined}
                    variant={isCompanyTab ? 'selected' : 'default'}
                  >
                    {t('route.company')}
                    <ChevronDownIcon display={{ base: 'none', md: 'none', lg: 'block' }} />
                  </Link>
                </PopoverTrigger>
                <Portal
                  _focus={{
                    border: 0,
                  }}
                >
                  <PopoverContent
                    zIndex='999 !important'
                    w='210px'
                    border='none'
                    borderRadius='12px'
                    _focus={{
                      outline: 0,
                    }}
                  >
                    <PopoverBody bg='bg_main_input' border='none' borderRadius='10px'>
                      {isAdmin ? (
                        <>
                          <DropDownNavLink
                            fontWeight='normal'
                            w='100%'
                            whiteSpace='nowrap'
                            textAlign='left'
                            cursor={isUploading ? 'not-allowed' : 'pointer'}
                            onClick={() => {
                              onClose();
                              setIsCompanyManagementOpen(true);
                            }}
                          >
                            {t('route.myCompany')}
                          </DropDownNavLink>
                        </>
                      ) : (
                        <></>
                      )}
                      <DropDownNavLink
                        fontWeight='normal'
                        w='100%'
                        textAlign='left'
                        as={ReactLink}
                        to={isUploading ? '#' : '/change-password'}
                        cursor={isUploading ? 'not-allowed' : 'pointer'}
                        onClick={onClose}
                      >
                        {t('route.changePassword')}
                      </DropDownNavLink>
                      <DropDownNavLink
                        fontWeight='normal'
                        w='100%'
                        textAlign='left'
                        onClick={() => {
                          dispatch(updateShowTermsStatus(true));
                        }}
                      >
                        {t('route.termOfService')}
                      </DropDownNavLink>
                      <DropDownNavLink
                        fontWeight='normal'
                        w='100%'
                        textAlign='left'
                        onClick={() => {
                          logout();
                        }}
                      >
                        {t('route.logOut')}
                      </DropDownNavLink>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </>
            )}
          </Popover>
          <LanguageSelector />
          <ThemeSwitch />
          <Box pos='relative'>
            <BellIcon
              boxSize={'1.5rem'}
              cursor={'pointer'}
              onClick={async () => {
                setIsOpen(!isOpen);
                if (num_as_read > 0) {
                  dispatch(homeAction.getMarkAsReadNotification());
                }
                // Clear new notification flag when opening drawer
                if (!isOpen) {
                  clearNewNotificationFlag();
                }

                // Request notification permission for Edge when opening drawer
                if (!isOpen && 'Notification' in window) {
                  const permission = Notification.permission;
                  const isEdge = navigator.userAgent.includes('Edg');

                  if (isEdge && (permission === 'default' || permission === undefined)) {
                    try {
                      if (typeof Notification.requestPermission === 'function') {
                        await Notification.requestPermission();
                      }
                    } catch (error) {
                      console.error('Failed to request notification permission:', error);
                    }
                  }
                }
              }}
            />
            {notificationCount.unread > 0 && (
              <Badge
                pos='absolute'
                zIndex='1'
                top={notificationCount.unread > 99 ? '-8px' : '-5px'}
                right={notificationCount.unread > 99 ? '-10px' : '-5px'}
                alignItems='center'
                justifyContent='center'
                display='flex'
                minW='16px'
                h='16px'
                px='1'
                color='white'
                fontSize='10px'
                fontWeight='bold'
                bg='red.500'
                borderRadius='full'
                shadow='0 0 0 1px white'
              >
                {notificationCount.unread > 99 ? '99+' : notificationCount.unread}
              </Badge>
            )}
          </Box>
          {num_as_read > 0 ? (
            <Badge ml='1' colorScheme='red'>
              {num_as_read}
            </Badge>
          ) : (
            <></>
          )}
          <IconButton
            display={{
              base: 'flex',
              md: 'none',
            }}
            ml='4'
            fontSize='32px'
            bg='transparent'
            aria-label='Open menu'
            icon={<HamburgerIcon color='amatrium.800' />}
            onClick={mobileNav.onOpen}
            size='sm'
            variant='ghost'
          />
        </Flex>

        <MobileNavContent isOpen={mobileNav.isOpen} onClose={mobileNav.onClose}>
          <Stack spacing={5}>
            <Flex>{children.find((child) => child.type === Brand)?.props.children}</Flex>
            <Stack>{children.find((child) => child.type === Links)?.props.children}</Stack>

            {/* Upload Menu Items for Mobile - Admin/SuperAdmin only */}
            {canUpload && (
              <>
                <Divider />
                <Stack spacing={2}>
                  <NavLink
                    as={ReactLink}
                    to={isUploading ? '#' : '/upload'}
                    isActive={location.pathname === '/upload'}
                    cursor={isUploading ? 'not-allowed' : 'pointer'}
                    onClick={mobileNav.onClose}
                  >
                    {t('route.manualUpload')}
                  </NavLink>
                  {isAdmin && (
                    <>
                      <NavLink
                        as={ReactLink}
                        to={isUploading ? '#' : '/sync-sharepoint'}
                        isActive={location.pathname === '/sync-sharepoint'}
                        cursor={isUploading ? 'not-allowed' : 'pointer'}
                        onClick={mobileNav.onClose}
                      >
                        {t('route.connection')}{' '}
                        <Badge
                          ml='2'
                          fontSize={'xx-small'}
                          textTransform={'capitalize'}
                          colorScheme='green'
                          rounded={4}
                        >
                          {t('common.beta')}
                        </Badge>
                      </NavLink>
                      <NavLink
                        as={ReactLink}
                        to={isUploading ? '#' : '/synchronization'}
                        isActive={location.pathname === '/synchronization'}
                        cursor={isUploading ? 'not-allowed' : 'pointer'}
                        onClick={mobileNav.onClose}
                      >
                        {t('route.synchronization')}{' '}
                        <Badge
                          ml='2'
                          fontSize={'xx-small'}
                          textTransform={'capitalize'}
                          colorScheme='green'
                          rounded={4}
                        >
                          {t('common.beta')}
                        </Badge>
                      </NavLink>
                    </>
                  )}
                </Stack>
              </>
            )}

            {/* Management System for Mobile - SuperAdmin only */}
            {checkAuthenticationSuperAdmin() && (
              <>
                <Divider />
                <NavLink
                  as={ReactLink}
                  to={'/management-system'}
                  isActive={location.pathname === '/management-system'}
                  onClick={mobileNav.onClose}
                >
                  {t('route.managementSystem')}
                </NavLink>
              </>
            )}

            <Divider />
            {/* Company Menu Items for Mobile */}
            <Stack spacing={2}>
              {isAdmin && (
                <NavLink
                  onClick={() => {
                    mobileNav.onClose();
                    setIsCompanyManagementOpen(true);
                  }}
                >
                  {t('route.myCompany')}
                </NavLink>
              )}
              <NavLink
                as={ReactLink}
                to={'/change-password'}
                isActive={location.pathname === '/change-password'}
                onClick={mobileNav.onClose}
              >
                {t('route.changePassword')}
              </NavLink>
              <NavLink
                onClick={() => {
                  mobileNav.onClose();
                  dispatch(updateShowTermsStatus(true));
                }}
              >
                {t('route.termOfService')}
              </NavLink>
              <NavLink
                onClick={() => {
                  mobileNav.onClose();
                  logout();
                }}
              >
                {t('route.logOut')}
              </NavLink>
            </Stack>
            <Divider />
            <Flex>{children.find((child) => child.type === UserProfile)?.props.children}</Flex>
          </Stack>
        </MobileNavContent>
      </Flex>
      <Notification isOpen={isOpen} onClose={onClose} />

      {/* Company Management Modal - Only for Admin/SuperAdmin */}
      {isAdmin && <User isOpen={isCompanyManagementOpen} onClose={onCompanyManagementClose} />}
    </Box>
  );
};
export const DropDownNavLink = (props) => {
  const { isActive, ...rest } = props;
  return (
    <Link
      display='block'
      px={{ base: '0.625rem', '2xl': '0.9rem' }}
      py={{ base: '0.625rem', '2xl': '0.8rem' }}
      fontSize={16}
      lineHeight='1.1rem'
      borderRadius='md'
      transition='all 0.3s'
      aria-current={isActive ? 'page' : undefined}
      variant={'dropdown'}
      {...rest}
    />
  );
};

export const NavLink = (props) => {
  const { isActive, ...rest } = props;
  return (
    <Link
      display='block'
      px={{ base: '0.625rem', '2xl': '0.9rem' }}
      py={{ base: '0.625rem', '2xl': '0.8rem' }}
      fontSize={16}
      lineHeight='1.1rem'
      borderRadius='md'
      transition='all 0.3s'
      aria-current={isActive ? 'page' : undefined}
      variant={isActive ? 'selected' : 'default'}
      {...rest}
    />
  );
};

const Brand = () => null;

const Links = () => null;

const UserProfile = () => null;

export const Navbar = Object.assign(Template, {
  Brand,
  Links,
  UserProfile,
});
